import React, { useCallback, useEffect, useState } from 'react';
import { Box, ToggleButton, ToggleButtonGroup } from '@mui/material';
import platformMcpService from '../../services/platformMcpService';
import platformAdminService from '../../services/platformAdminService';
import { describeApiError } from '../../utils/apiError';
import PlatformMcpActivityTable from '../shared/PlatformMcpActivity/PlatformMcpActivityTable';
import PlatformMcpServerSection from './PlatformMcpServerSection';
import PlatformMcpToolsSection from './PlatformMcpToolsSection';
import mcpStyles from './PlatformMcpPanel.module.scss';

// Module-level so its identity is stable (the activity table loads on change).
const platformCompanyOptions = async () => {
  const data = await platformAdminService.listCompanies(false, { limit: 200 });
  return data?.companies || (Array.isArray(data) ? data : []);
};

const SECTIONS = [
  { value: 'server', label: 'Server' },
  { value: 'tools', label: 'Tools' },
  { value: 'activity', label: 'Activity' },
];

/**
 * Platform admin "Platform MCP" tab (spec screen B): the shared Quadroid
 * server's status, setup, on/off, rate limit, tools, and all activity.
 * The server never appears in the "MCP Servers" screen; it is managed here.
 */
const PlatformMcpPanel = ({ notify, partners = null, companyLabelFn = null }) => {
  const [section, setSection] = useState('server');
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setStatus((await platformMcpService.getStatus()) || null);
    } catch (err) {
      setError(describeApiError(err, "Couldn't load the Quadroid server status."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  return (
    <Box className={mcpStyles.section}>
      <ToggleButtonGroup
        size="small"
        exclusive
        value={section}
        onChange={(_, v) => v && setSection(v)}
        className={mcpStyles.sectionToggle}
      >
        {SECTIONS.map((s) => (
          <ToggleButton key={s.value} value={s.value}>
            {s.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      {section === 'server' && (
        <PlatformMcpServerSection
          status={status}
          loading={loading}
          error={error}
          onReload={loadStatus}
          notify={notify}
        />
      )}
      {section === 'tools' && (
        <PlatformMcpToolsSection exists={!!status?.exists} notify={notify} onChanged={loadStatus} />
      )}
      {section === 'activity' && (
        <PlatformMcpActivityTable
          scope="platform"
          partners={partners}
          loadCompanyOptions={platformCompanyOptions}
          companyLabelFn={companyLabelFn}
          showError
        />
      )}
    </Box>
  );
};

export default PlatformMcpPanel;
